import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { setPlayingOnBanner, setPlayingOnCorner, setPlayingOnPlayground } from "../../../../redux/Slices/deviceSlice";
import { usePreviewCampaignContent } from "../../../../queries";
import { MediaPlayer } from "./Custom/MediaPlayer";
import LayoutPreview from "./Custom/LayoutPreview";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "react-query";
import { LazyLoadImage } from "react-lazy-load-image-component";
import AnimatedWrap from "../../../SharedComponents/DragDrop/animateMedia";
import { useTranslation } from "react-i18next";

const ContentPreview = ({ currentPlaygroundPlaying, currentBannerPlaying, currentCornerPlaying, currentMediaPlaying, campaignId, deviceId }) => {
    const { t } = useTranslation();
    const { isLoading, data } = usePreviewCampaignContent(campaignId, {
        enabled: !!campaignId,
    });
    const dispatch = useDispatch()
    const queryClient = useQueryClient();
    const { paused } = useSelector((state) => state.device);

    const flattenMainItems = [];
    const flattenPlaygroundItems = [];
    const flattenCornerItems = [];
    const flattenBannerItems = [];



    data?.MAIN?.forEach(item => {
        flattenMainItems.push(item);
    });

    data?.PLAYGROUND?.forEach(item => {
        flattenPlaygroundItems.push(item);
    });

    data?.CORNER?.forEach(item => {
        flattenCornerItems.push(item);
    });

    data?.BANNER?.forEach(item => {
        flattenBannerItems.push(item);
    });

    const startTimestamp = currentMediaPlaying?.start_time?.valueOf?.() ?? currentMediaPlaying?.start_time;
    const startTimestampPlayground = currentPlaygroundPlaying?.start_time?.valueOf?.() ?? currentPlaygroundPlaying?.start_time;
    const startTimestampBanner = currentBannerPlaying?.start_time?.valueOf?.() ?? currentBannerPlaying?.start_time;
    const startTimestampCorner = currentCornerPlaying?.start_time?.valueOf?.() ?? currentCornerPlaying?.start_time;


    // Find currently playing MAIN item
    const playingItem = flattenMainItems.find(
        media =>
            media.assigned_id === currentMediaPlaying.currentMediaId ||
            // media.playlist_id === currentMediaPlaying.currentMediaId ||
            media.layout_id === currentMediaPlaying.currentMediaId
    );
    const currentPlayground = flattenPlaygroundItems.find(
        media =>
            media.assigned_id === currentPlaygroundPlaying.id ||
            // media.playlist_id === currentPlaygroundPlaying.id ||
            media.layout_id === currentPlaygroundPlaying.id
    );

    const currentCorner = flattenCornerItems.find(
        media =>
            media.assigned_id === currentCornerPlaying.id ||
            // media.playlist_id === currentCornerPlaying.id ||
            media.layout_id === currentCornerPlaying.id
    );

    const currentBanner = flattenBannerItems.find(
        media =>
            media.assigned_id === currentBannerPlaying.id ||
            // media.playlist_id === currentBannerPlaying.id ||
            media.layout_id === currentBannerPlaying.id
    );

    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    useEffect(() => {
        if (!currentMediaPlaying?.start_time) return;

        const update = () => {
            const now = paused ? moment(currentMediaPlaying?.pause_start_time) : moment();
            setElapsedSeconds(now.diff(moment(currentMediaPlaying?.start_time), "seconds"));
        };

        update();

        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [paused, currentMediaPlaying?.start_time, currentMediaPlaying?.pause_start_time]);


    useEffect(() => {

        if (currentPlayground) {
            dispatch(setPlayingOnPlayground(currentPlayground?.assigned_id || currentPlayground?.playlist_id || currentPlayground?.layout_id))
        } else {
            dispatch(setPlayingOnPlayground(""))
        }
    }, [currentPlayground])

    useEffect(() => {
        if (currentBanner) {
            dispatch(setPlayingOnBanner(currentBanner?.assigned_id || currentBanner?.playlist_id || currentBanner?.layout_id))
        } else {
            dispatch(setPlayingOnBanner(""))
        }
    }, [currentBanner])

    useEffect(() => {
        if (currentCorner) {
            dispatch(setPlayingOnCorner(currentCorner?.assigned_id || currentCorner?.playlist_id || currentCorner?.layout_id))
        } else {
            dispatch(setPlayingOnCorner(""))
        }
    }, [currentCorner])

    useEffect(() => {
        return () => {
            queryClient.removeQueries(["previewCampaignContent", campaignId, deviceId]);
            dispatch(setPlayingOnCorner(""))
            dispatch(setPlayingOnBanner(""))
            dispatch(setPlayingOnPlayground(""))

        };
    }, []);

    const memoizedMainMedia = useMemo(() => {
        let elapsedSecond
        const now = paused ? moment(currentMediaPlaying?.pause_start_time) : moment();
        elapsedSecond = now.diff(moment(startTimestamp), "seconds")
        return playingItem ? <MediaPlayer key={`${playingItem?.assigned_id}-${startTimestamp}`}
            data={playingItem} elapsedSeconds={elapsedSecond}
            customDuration={parseFloat(playingItem?.custom_duration) || 0}
        /> : null;
    }, [playingItem?.assigned_id, startTimestamp]);


    const memoizedPlaygroundMedia = useMemo(() => {
        let elapsedSecond
        const now = paused ? moment(currentMediaPlaying?.pause_start_time) : moment();
        elapsedSecond = now.diff(moment(startTimestampPlayground), "seconds")
        return currentPlayground ? <MediaPlayer key={`${currentPlayground?.assigned_id}-${startTimestampPlayground}`}
            data={currentPlayground} elapsedSeconds={elapsedSecond}
            customDuration={parseFloat(currentPlayground?.custom_duration) || 0}
        /> : null;
    }, [startTimestampPlayground, currentPlayground?.assigned_id, currentMediaPlaying?.pause_start_time]);

    const memoizedCornerMedia = useMemo(() => {
        let elapsedSecond
        const now = paused ? moment(currentMediaPlaying?.pause_start_time) : moment();
        elapsedSecond = now.diff(moment(startTimestampCorner), "seconds")
        return currentCorner ? <MediaPlayer key={`${currentCorner?.assigned_id}-${startTimestampCorner}`}
            data={currentCorner} elapsedSeconds={elapsedSecond}
            customDuration={parseFloat(currentCorner?.custom_duration) || 0}
        /> : null;
    }, [startTimestampCorner, currentCorner?.assigned_id, currentMediaPlaying?.pause_start_time]);

    const memoizedBannerMedia = useMemo(() => {
        let elapsedSecond
        const now = paused ? moment(currentMediaPlaying?.pause_start_time) : moment();
        elapsedSecond = now.diff(moment(startTimestampBanner), "seconds")
        return currentBanner ? <MediaPlayer key={`${currentBanner.assigned_id}-${startTimestampBanner}`}
            data={currentBanner} elapsedSeconds={elapsedSecond}
            customDuration={parseFloat(currentBanner.custom_duration) || 0}
        /> : null;
    }, [startTimestampBanner, currentBanner?.assigned_id, currentMediaPlaying?.pause_start_time]);




    if (isLoading) return <div>{t("devices.preview_loading")}</div>;
    if (!data) return <div>{t("devices.preview_no_data")}</div>;
    return (
        <div className="relative w-full h-full overflow-hidden  text-white font-bold text-xl rounded-lg">
            {playingItem?.layout_id ?
                <>
                    <LayoutPreview layout={playingItem} pause_start_time={currentMediaPlaying?.pause_start_time} startTime={startTimestamp} />
                </>
                :

                <>
                    {playingItem && (
                        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-10 rounded-lg">
                            {data?.campaign_settings?.live?.media_animation?.enabled ?
                                <AnimatedWrap
                                    itemKey={`${playingItem?.assigned_id || playingItem?.layout_id}-${startTimestamp}`}
                                    type={data?.campaign_settings?.live?.media_animation?.type || "FADE"}
                                    direction={data?.campaign_settings?.live?.media_animation?.direction || "CENTER"}
                                    durationMs={data?.campaign_settings?.live?.media_animation?.duration * 1000 || 600}
                                    className="absolute inset-0 z-10 rounded-lg"
                                >
                                    {memoizedMainMedia}
                                </AnimatedWrap>
                                :
                                memoizedMainMedia
                            }
                        </div>
                    )}

                    {currentPlayground && playingItem?.show_layer_one && (
                        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-20 rounded-lg">
                            {currentPlayground.layout_id ? (
                                <LayoutPreview
                                    layout={currentPlayground}
                                    pause_start_time={currentMediaPlaying?.pause_start_time}
                                    startTime={startTimestampPlayground}
                                />
                            ) : currentPlayground?.media_type === "IMAGE" || currentPlayground?.media_type === "CORNER" ? (
                                <div className="w-full h-full">
                                    <div
                                        id="resizable"
                                        key="resizable"
                                        className="overflow-hidden absolute cursor-pointer resizable media-preview-corner"
                                        style={{
                                            left: `${currentPlayground?.settings?.start_margin ?? 0}%`,
                                            top: `${currentPlayground?.settings?.top_margin ?? 0}%`,
                                            width: `${currentPlayground?.settings?.width ?? 100}%`,
                                            height: `${currentPlayground?.settings?.height ?? 100}%`,
                                        }}
                                    >
                                        {data?.campaign_settings?.live?.playground_media_animation?.enabled ?
                                            <AnimatedWrap
                                                itemKey={`${playingItem?.assigned_id || playingItem?.layout_id}-${startTimestampPlayground}`}
                                                type={data?.campaign_settings?.live?.playground_media_animation?.type || "FADE"}
                                                direction={data?.campaign_settings?.live?.playground_media_animation?.direction || "CENTER"}
                                                durationMs={data?.campaign_settings?.live?.playground_media_animation?.duration * 1000 || 600}
                                                className="absolute inset-0 z-10 rounded-lg"
                                            >
                                                {memoizedPlaygroundMedia}
                                            </AnimatedWrap>
                                            :
                                            memoizedPlaygroundMedia

                                        }
                                    </div>
                                </div>
                            ) : (
                                data?.campaign_settings?.live?.playground_media_animation?.enabled ?
                                    <AnimatedWrap
                                        itemKey={`${playingItem?.assigned_id || playingItem?.layout_id}-${startTimestampPlayground}`}
                                        type={data?.campaign_settings?.live?.playground_media_animation?.type || "FADE"}
                                        direction={data?.campaign_settings?.live?.playground_media_animation?.direction || "CENTER"}
                                        durationMs={data?.campaign_settings?.live?.playground_media_animation?.duration * 1000 || 600}
                                        className="absolute inset-0 z-10 rounded-lg"
                                    >
                                        {memoizedPlaygroundMedia}
                                    </AnimatedWrap>
                                    :
                                    memoizedPlaygroundMedia


                            )}
                        </div>
                    )}



                    {currentCorner && playingItem?.show_layer_two && (
                        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-30 rounded-lg">
                            {currentCorner.media_type == "IMAGE" || currentCorner.media_type == "CORNER" ?
                                <div className="w-full h-full">
                                    <div
                                        id={`resizable`}
                                        key={`resizable`}
                                        className='overflow-hidden absolute cursor-pointer resizable media-preview-corner'
                                        style={{
                                            left: `${currentCorner?.settings?.start_margin ?? 0}%`,
                                            top: `${currentCorner?.settings?.top_margin ?? 0}%`,
                                            width: `${currentCorner?.settings?.width ?? 100}%`,
                                            height: `${currentCorner?.settings?.height ?? 100}%`,
                                        }}
                                    >
                                        {data?.campaign_settings?.live?.corner_media_animation?.enabled ?
                                            <AnimatedWrap
                                                itemKey={`${playingItem?.assigned_id || playingItem?.layout_id}-${startTimestampCorner}`}
                                                type={data?.campaign_settings?.live?.corner_media_animation?.type || "FADE"}
                                                direction={data?.campaign_settings?.live?.corner_media_animation?.direction || "CENTER"}
                                                durationMs={data?.campaign_settings?.live?.corner_media_animation?.duration * 1000 || 600}
                                                className="absolute inset-0 z-10 rounded-lg"
                                            >
                                                <LazyLoadImage src={currentCorner.path} style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: currentCorner?.maintain_aspect_ratio ? 'contain' : 'fill',
                                                }} />
                                            </AnimatedWrap>
                                            :
                                            <LazyLoadImage src={currentCorner.path} style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: currentCorner?.maintain_aspect_ratio ? 'contain' : 'fill',
                                            }} />
                                        }
                                    </div>
                                </div>
                                :
                                data?.campaign_settings?.live?.corner_media_animation?.enabled ?
                                    <AnimatedWrap
                                        itemKey={`${playingItem?.assigned_id || playingItem?.layout_id}-${startTimestampCorner}`}
                                        type={data?.campaign_settings?.live?.corner_media_animation?.type || "FADE"}
                                        direction={data?.campaign_settings?.live?.corner_media_animation?.direction || "CENTER"}
                                        durationMs={data?.campaign_settings?.live?.corner_media_animation?.duration * 1000 || 600}
                                        className="absolute inset-0 z-10 rounded-lg"
                                    >
                                        {memoizedCornerMedia}
                                    </AnimatedWrap>
                                    :
                                    memoizedCornerMedia
                            }
                        </div>
                    )}

                    {currentBanner && playingItem?.show_layer_three && (
                        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-40 rounded-lg">
                            {data?.campaign_settings?.live?.banner_media_animation?.enabled ?
                                <AnimatedWrap
                                    itemKey={`${playingItem?.assigned_id || playingItem?.layout_id}-${startTimestampBanner}`}
                                    type={data?.campaign_settings?.live?.banner_media_animation?.type || "FADE"}
                                    direction={data?.campaign_settings?.live?.banner_media_animation?.direction || "CENTER"}
                                    durationMs={data?.campaign_settings?.live?.banner_media_animation?.duration * 1000 || 600}
                                    className="absolute inset-0 z-10 rounded-lg"
                                >
                                    {memoizedBannerMedia}
                                </AnimatedWrap>
                                :
                                memoizedBannerMedia
                            }
                        </div>
                    )}
                </>
            }
        </div >
    );
};

export default ContentPreview;

